import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarDays, Clock, Plus, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AvailabilityEditor — lets a registered attendee publish the periods during
 * an event when they're free to network. Any peer's Request-to-Meet attempt
 * is restricted to time inside one of the attendee's published windows.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   event: { event_id, title, date, meeting_durations }
 *   onSaved: (windows) => void
 */
export default function AvailabilityEditor({ open, onClose, event, onSaved }) {
  const defaultDate = (event?.date || new Date().toISOString()).slice(0, 10);
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !event?.event_id) return;
    setLoading(true);
    fetch(`/api/events/${event.event_id}/availability/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { windows: [] })
      .then(d => setWindows(d.windows || []))
      .finally(() => setLoading(false));
  }, [open, event?.event_id]);

  const updateRow = (idx, patch) => {
    setWindows(prev => prev.map((w, i) => i === idx ? { ...w, ...patch } : w));
  };
  const addRow = () => {
    setWindows(prev => [...prev, { date: defaultDate, start_time: '10:00', end_time: '12:00' }]);
  };
  const removeRow = (idx) => {
    setWindows(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    // Client-side guard — the backend also validates + collapses overlaps.
    for (const w of windows) {
      if (!w.date || !w.start_time || !w.end_time) {
        toast.error('Each window needs a date, start and end time.'); return;
      }
      if (w.end_time <= w.start_time) {
        toast.error('End time must come after start time.'); return;
      }
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/events/${event.event_id}/availability/me`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ windows }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save availability');
      toast.success(data.windows.length > 0
        ? 'Networking availability saved.'
        : 'Availability cleared — any time can now be requested.');
      onSaved?.(data.windows);
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const durationHint = (event?.meeting_durations || [15, 30]).map(d => `${d} min`).join(' or ');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="availability-editor-dialog">
        <DialogHeader>
          <DialogTitle>Set when you're free to meet</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Add one or more windows during the event. Other attendees can only request a {durationHint} meeting inside a window you publish.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {loading ? (
            <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
          ) : windows.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-center" data-testid="availability-empty">
              <Info className="h-5 w-5 mx-auto text-slate-400 mb-1" />
              <p className="text-xs text-slate-500 mb-3">You haven't published any windows yet. Until you do, anyone can request a meeting at any time.</p>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="availability-add-first">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add a window
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {windows.map((w, i) => (
                <div key={i} className="rounded-md border border-slate-200 p-2.5 space-y-2" data-testid={`availability-row-${i}`}>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label htmlFor={`win-date-${i}`} className="text-[10px] uppercase tracking-wider text-slate-400">Date</Label>
                      <div className="relative">
                        <CalendarDays className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <Input id={`win-date-${i}`} data-testid={`availability-date-${i}`} type="date"
                          value={w.date} onChange={e => updateRow(i, { date: e.target.value })}
                          className="pl-7 h-8 text-xs" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor={`win-start-${i}`} className="text-[10px] uppercase tracking-wider text-slate-400">From</Label>
                      <div className="relative">
                        <Clock className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <Input id={`win-start-${i}`} data-testid={`availability-start-${i}`} type="time"
                          value={w.start_time} onChange={e => updateRow(i, { start_time: e.target.value })}
                          className="pl-7 h-8 text-xs" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor={`win-end-${i}`} className="text-[10px] uppercase tracking-wider text-slate-400">Until</Label>
                      <div className="relative">
                        <Clock className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <Input id={`win-end-${i}`} data-testid={`availability-end-${i}`} type="time"
                          value={w.end_time} onChange={e => updateRow(i, { end_time: e.target.value })}
                          className="pl-7 h-8 text-xs" />
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeRow(i)}
                    data-testid={`availability-remove-${i}`}
                    className="text-xs text-slate-400 hover:text-rose-600 inline-flex items-center gap-1">
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addRow} data-testid="availability-add-more" className="w-full">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add another window
              </Button>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="availability-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="availability-save"
              className="bg-brand-navy hover:bg-brand-dark text-white">
              {saving ? 'Saving…' : 'Save availability'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
